import React from 'react';
import { Typography, Box, Stack } from '@mui/material';
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Page,
  Selection,
  Inject,
} from '@syncfusion/ej2-react-grids';

import { FeeScheduleView } from '../../../../../../services/ContractService/types/contractTypes';
import { SearchBar } from '../../shared';
import { useFeeScheduleSearch, useFeeScheduleNavigation } from './hooks';
import { effectiveDateTemplate, endDateTemplate } from './templates';

export interface FeeScheduleGridProps {
  title: string;
  feeSchedules: FeeScheduleView[];
  isLoading: boolean;
  error: unknown;
  contractId?: string;
  company?: string;
  actions?: React.ReactNode;
  testId?: string;
}

/**
 * FeeScheduleGrid - Displays fee schedules for a contract
 *
 * Follows the established grid pattern with:
 * - Separated templates for cell rendering
 * - Custom hooks for search and navigation
 * - Clean component structure
 */
export const FeeScheduleGrid: React.FC<FeeScheduleGridProps> = ({
  title,
  feeSchedules,
  isLoading,
  error,
  contractId,
  company,
  actions,
  testId,
}) => {
  // Use custom hooks
  const { searchTerm, filteredFeeSchedules, handleSearchChange, handleClearSearch } =
    useFeeScheduleSearch(feeSchedules);
  const { handleRowClick } = useFeeScheduleNavigation(company);

  if (isLoading) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body1">Loading {title.toLowerCase()}...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body1" color="error">
          Error loading {title.toLowerCase()}. Please try again.
        </Typography>
      </Box>
    );
  }

  if (!contractId) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No contract selected.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}
      >
        <Typography variant="h6">{title}</Typography>
        {actions && <Box>{actions}</Box>}
      </Box>

      {/* Search Row */}
      <Box sx={{ mb: 2 }}>
        <SearchBar
          value={searchTerm}
          onChange={handleSearchChange}
          onClear={handleClearSearch}
          placeholder="Search descriptions, categories, types..."
        />
      </Box>

      {filteredFeeSchedules.length > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {filteredFeeSchedules.length} fee schedule
          {filteredFeeSchedules.length !== 1 ? 's' : ''} found
        </Typography>
      )}

      <GridComponent
        dataSource={filteredFeeSchedules}
        allowPaging={true}
        allowSorting={true}
        allowSelection={true}
        pageSettings={{ pageSize: 10, pageSizes: [10, 25, 50] }}
        selectionSettings={{ mode: 'Row', type: 'Single' }}
        height="400"
        width="100%"
        enableVirtualization={false}
        rowSelected={handleRowClick}
        data-testid={testId}
      >
        <ColumnsDirective>
          <ColumnDirective field="description" headerText="Description" width="200" />
          <ColumnDirective field="category" headerText="Category" width="150" />
          <ColumnDirective field="feeType" headerText="Fee Type" width="120" />
          <ColumnDirective
            field="effectiveDate"
            headerText="Effective Date"
            width="130"
            template={effectiveDateTemplate}
          />
          <ColumnDirective
            field="endDate"
            headerText="End Date"
            width="130"
            template={endDateTemplate}
          />
        </ColumnsDirective>
        <Inject services={[Page, Selection]} />
      </GridComponent>
    </Box>
  );
};

export default FeeScheduleGrid;
